import { useState, useEffect } from 'react';
import { FileUpload } from '../../api';
import { getErrorMessage } from '../../../../shared/src';
import { formatFileSize } from '../../utils/fileHash';

interface FileDetailsPanelProps {
  file: FileUpload;
  onClose: () => void;
  onUpdate: (fileId: string, data: { description?: string }) => Promise<void>;
}

export function FileDetailsPanel({ file, onClose, onUpdate }: FileDetailsPanelProps) {
  const [description, setDescription] = useState(file.description || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [hasChanges, setHasChanges] = useState(false);

  // Reset state when file changes
  useEffect(() => {
    setDescription(file.description || '');
    setHasChanges(false);
    setError('');
  }, [file.id, file.description]);

  const handleDescriptionChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setDescription(e.target.value);
    setHasChanges(e.target.value !== (file.description || ''));
  };

  const handleSave = async () => {
    if (!hasChanges) return;

    try {
      setSaving(true);
      setError('');
      await onUpdate(file.id, { description: description || undefined });
      setHasChanges(false);
    } catch (err: unknown) {
      setError(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const handleBlur = () => {
    if (hasChanges) {
      handleSave();
    }
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString();
  };

  return (
    <div className="file-details-panel">
      <div className="panel-header">
        <h3>File Details</h3>
        <button className="panel-close" onClick={onClose} title="Close">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M18 6L6 18M6 6l12 12" />
          </svg>
        </button>
      </div>

      <div className="panel-content">
        <div className="detail-group">
          <label>Name</label>
          <div className="detail-value" title={file.file_name}>
            {file.file_name}
          </div>
        </div>

        {file.original_name !== file.file_name && (
          <div className="detail-group">
            <label>Original Name</label>
            <div className="detail-value detail-secondary">{file.original_name}</div>
          </div>
        )}

        <div className="detail-group">
          <label>Size</label>
          <div className="detail-value">{formatFileSize(file.file_size)}</div>
        </div>

        <div className="detail-group">
          <label>Type</label>
          <div className="detail-value">{file.content_type}</div>
        </div>

        <div className="detail-group">
          <label>Uploaded By</label>
          <div className="detail-value">
            {file.uploaded_by_first_name} {file.uploaded_by_last_name}
            <span className="detail-secondary"> ({file.uploaded_by_email})</span>
          </div>
        </div>

        <div className="detail-group">
          <label>Uploaded</label>
          <div className="detail-value">{formatDate(file.completed_at)}</div>
        </div>

        <div className="detail-group">
          <label>
            Description
            {hasChanges && <span className="unsaved-indicator">*</span>}
          </label>
          <textarea
            value={description}
            onChange={handleDescriptionChange}
            onBlur={handleBlur}
            placeholder="Describe what this file contains..."
            rows={4}
            disabled={saving}
          />
          {error && <span className="form-error">{error}</span>}
          {saving && <span className="saving-indicator">Saving...</span>}
        </div>
      </div>
    </div>
  );
}
